import { sharedIntegrationsAvailable } from '@mygpt/app/config/integrations';
import { LoadingOverlay, ScrollArea, Tabs } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import dayjs from 'dayjs';
import equal from 'fast-deep-equal';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BotQuota,
  getBotQuota,
  getRobot,
  RobotRequestConfigs,
  RobotRequestFormConfigs,
  updateRobot,
  updateRobotConfigs,
  UpdateRobotParams,
  useBot,
  useCollaborateStore,
  AIType,
  RobotType,
  CanAccess,
  getValidModelValue,
} from '@mygpt/app';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import { Advanced } from '@/components/Settings/items/Advanced';
import { CanvasForm } from '@/components/Settings/items/Canvas';
import { General } from '@/components/Settings/items/General';
import { Prompt } from '@/components/Settings/items/Prompt';
import { Publish } from '@/components/Settings/items/Publish';
import { Consent } from '@/components/Settings/items/Consent';
import { languageTranslate } from '@/constant';
import { useSubmitFormAsync } from '@/hooks/useSubmitFormAsync';
import { useGPTStore } from '@/store';
import { convertBooleanType } from '@/utils/convertBooleanType';

const Settings = () => {
  const canvasAvailable =
    sharedIntegrationsAvailable && (window as any).ENV?.GBASE_SUPPORT_FRONTEND_HIDE_CANVAS !== 'true';
  const { t, i18n } = useTranslation();
  const { botId } = useBot();
  const { setRobotInfo: setStoreRobotInfo, userInfo, getQuotaInfo, quotaInfo, configInfo } = useGPTStore();
  const { isAssist } = useCollaborateStore();

  const [activeTab, setActiveTab] = useState<string | null>('general');

  const [loading, setLoading] = useState(true);

  const [robotInfo, setRobotInfo] = useState<UpdateRobotParams>();
  // 本地部署模式的权限类型（用于控制危险区域可见性）
  const [permissionType, setPermissionType] = useState<string>();
  const [robotPlanInfo, setRobotPlanInfo] = useState<BotQuota>();
  const [datasetId, setDatasetId] = useState<string>();
  const [robotConfigs, setRobotConfigs] = useState<RobotRequestFormConfigs>();
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      setActiveTab(hash || 'general');
    };

    // 初始化时设置 activeTab
    handleHashChange();

    // 监听 hash 变化
    window.addEventListener('hashchange', handleHashChange);

    // 清理事件监听器
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
  const canUsedMessage = useMemo(() => {
    if (!quotaInfo) return 0;
    return quotaInfo.effective_quotas.message_quota.available;
  }, [quotaInfo]);

  const defaultModel = useMemo(() => {
    const defaultModels = configInfo?.default_models.filter((model) => model.enabled) || [];
    return defaultModels.length > 0 ? defaultModels[0].value : '';
  }, [configInfo]);

  const digitalHumanAccess = useMemo(() => {
    // const inviteValue = userInfo?.userconfigs.find((config) => config.key === 'enable_digital_human_interface');
    // return inviteValue && inviteValue.value === 'True';
    return robotInfo?.robot_type !== 'rag';
  }, [robotInfo]);

  const robotBaseInfoForm = useForm<UpdateRobotParams>({
    initialValues: {
      name: '',
      subject_name: '',
      aiId: '',
      discrible: '',
      ai_type: 'private' as AIType,
      robot_type: 'rag' as RobotType,
      prompt: '',
    },
    validate: {
      name: (value) => {
        if (value?.trim() !== '') {
          return null;
        } else {
          const error = t('fields.name_strength');
          showNotification({ message: error, color: 'red' });
          return error;
        }
      },
      discrible: (value) => {
        if (value?.trim() !== '') {
          return null;
        } else {
          const error = t('create.description_required');
          showNotification({ message: error, color: 'red' });
          return error;
        }
      },
    },
  });

  const documentForm = useForm<RobotRequestFormConfigs>({
    initialValues: {
      source_lang: '',
      fallback_to_chatgpt: false,
      openai_model: defaultModel,
      recommend: false,
      notification_admin: false,
      max_questions: undefined,
      question_date: ['', ''],
      faq_search_answer_with_embedding: false,
      enable_images: false,
      enable_web_search: false,
      web_search_site_list: [],
      talking_style: 'default',
      chat_temperature: 0,
      unknown_text: '',
      digital_human_prompt: '',
      voice_agent_prompt: '',
      feedback_form: {
        content: '',
        forward_chat_history: false,
        forward_chat_history_visible_text: '',
        forward_chat_history_ai_instruction: '',
      },
      enable_access_control: false,
      access_control_allow_email_list: [],
      preview_panel_api: '',
      enable_tts: false,
      enable_2d_digital_person: false,
      qa_recommendation: true,
      disable_third_party_url_access: false,
      canvas_type: '',
      enable_llm_metrics: false,
      enable_thinking: false,
      enable_memory: false,
    },
    validate: {
      unknown_text: (value) => {
        if (!value) return null;
        if (value.trim().length >= 200) {
          const error = t('fields.max_character_limit', {
            max_size: 200,
          });
          return error;
        }
      },
    },
  });

  const submitDocument = async (values: RobotRequestFormConfigs, event: FormEvent<HTMLFormElement> | undefined) => {
    try {
      event && event.preventDefault();
      const {
        access_control_allow_email_list,
        chat_temperature,
        digital_human_prompt,
        voice_agent_prompt,
        enable_access_control,
        enable_images,
        enable_tts,
        enable_2d_digital_person,
        qa_recommendation,
        disable_third_party_url_access,
        enable_llm_metrics,
        enable_web_search,
        fallback_to_chatgpt,
        faq_search_answer_with_embedding,
        max_questions,
        notification_admin,
        openai_model,
        preview_panel_api,
        question_date,
        recommend,
        source_lang,
        talking_style,
        feedback_form,
        unknown_text,
        web_search_site_list,
        canvas_type,
        enable_thinking,
        enable_memory,
      } = values;

      // 如果和上次更新相比，没有任何配置项变化，那么直接退出
      const hasNotChanged = equal(values, robotConfigs);

      if (hasNotChanged) {
        return;
      }

      setLoading(true);

      // if (max_questions && +max_questions > canUsedMessage) {
      //   const errorMsg = t('errors.detail.max_questions', { number: canUsedMessage });
      //   showNotification({ message: errorMsg, color: 'red' });
      //   documentForm.setFieldError('max_questions', errorMsg);
      //   setLoading(false);
      //   return;
      // }

      const configs: RobotRequestConfigs = {
        fallback_to_chatgpt,
        openai_model,
        recommend,
        notification_admin,
        faq_search_answer_with_embedding,
        enable_images,
        enable_web_search,
        source_lang,
        talking_style,
        digital_human_prompt,
        voice_agent_prompt,
        unknown_text: unknown_text?.trim(),
        chat_temperature: (chat_temperature || 0) / 100,
        web_search_site_list: (Array.isArray(web_search_site_list) ? web_search_site_list : [web_search_site_list])
          .map((url) => (url || '').trim())
          .filter(Boolean),
        max_questions: max_questions || '',
        questions_start_date: question_date[0]
          ? dayjs(question_date[0]).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
          : '',
        questions_end_date: question_date[1]
          ? dayjs(question_date[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
          : '',
        feedback_form: {
          content: feedback_form.content || '',
          forward_chat_history: feedback_form.forward_chat_history || false,
          forward_chat_history_visible_text: feedback_form.forward_chat_history_visible_text || '',
          forward_chat_history_ai_instruction: feedback_form.forward_chat_history_ai_instruction || '',
        },
        enable_access_control,
        access_control_allow_email_list,
        preview_panel_api,
        enable_tts,
        enable_2d_digital_person,
        qa_recommendation,
        disable_third_party_url_access,
        canvas_type,
        enable_llm_metrics,
        enable_thinking,
        enable_memory,
      };

      await updateRobotConfigs({ aiId: botId, configs });

      showNotification({
        message: t('save_success'),
        color: 'green.5',
      });

      setRobotConfigs(values);
    } catch (error: any) {
      console.error(`submitDocument error: ${error}`);
      if (error && error?.response?.data) {
        const data = error?.response?.data;
        const { detail } = data;
        if (detail.startsWith('Invalid max questions')) {
          getQuotaInfo().then(() => {
            const errorMsg = t('errors.detail.max_questions', { number: canUsedMessage });
            documentForm.setFieldError('max_questions', errorMsg);
          });
        } else {
          // 其它后端校验失败（如 Canvas Webhook URL 非法）需向用户提示，避免配置静默丢失
          showNotification({
            color: 'red',
            message: detail || t('errors.common'),
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * 更新机器人基础信息
   */
  const updateRobotBaseInfo = async (values: UpdateRobotParams, event: FormEvent<HTMLFormElement> | undefined) => {
    event && event.preventDefault();
    const { hasErrors } = robotBaseInfoForm.validate();
    if (hasErrors) {
      return;
    }

    const { ai_type, discrible, name, robot_type, subject_name, prompt } = values;

    const hasNotChanged = equal(values, robotInfo);

    if (hasNotChanged) {
      return;
    }

    const params = {
      aiId: botId,
      name: name.trim(),
      subject_name: subject_name?.trim(),
      discrible: discrible.trim(),
      ai_type: ai_type,
      robot_type: robot_type as RobotType,
      prompt: prompt.trim(),
    };

    try {
      setLoading(true);
      const res = await updateRobot(params);

      showNotification({
        message: t('save_success'),
        color: 'green.5',
      });

      setStoreRobotInfo(res.data);
      setRobotInfo(params);
    } catch (error: any) {
      console.error(`updateRobotBaseInfo error: ${error}`);
      showNotification({ message: error.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const initRobotInfo = async () => {
    try {
      const { data } = await getRobot({ aiId: botId });

      // 保存权限类型（个人模式下无 permission_type 时默认视为 owner）
      setPermissionType((data as any).permission_type);

      const baseInfo = {
        name: data.name,
        subject_name: data.subject_name,
        discrible: data.discrible || '',
        ai_type: data.ai_type,
        aiId: botId,
        robot_type: data.robot_type,
        prompt: data.prompt || '',
      };
      setRobotInfo(baseInfo);
      data.datasets?.[0]?.id && setDatasetId(data.datasets[0].id);
      robotBaseInfoForm.setValues(baseInfo);

      const hasQuestionDateValue = data.configs?.questions_start_date && data.configs?.questions_end_date;

      const configs = {
        fallback_to_chatgpt: convertBooleanType(false, data.configs?.fallback_to_chatgpt),
        recommend: convertBooleanType(false, data.configs?.recommend),
        notification_admin: convertBooleanType(true, data.configs?.notification_admin),
        faq_search_answer_with_embedding: convertBooleanType(false, data.configs?.faq_search_answer_with_embedding),
        enable_images: convertBooleanType(false, data.configs?.enable_images),
        enable_web_search: convertBooleanType(false, data.configs?.enable_web_search),
        web_search_site_list: data.configs?.web_search_site_list || [],
        max_questions: data.configs?.max_questions ? Number(data.configs?.max_questions) : undefined,
        openai_model: getValidModelValue(data.configs?.openai_model),
        talking_style: data.configs?.talking_style,
        unknown_text: data.configs?.unknown_text,
        chat_temperature: (data.configs?.chat_temperature || 0) * 100,
        source_lang: data.configs.source_lang
          ? languageTranslate(data.configs.source_lang)
          : languageTranslate(data.user.locale),
        question_date: hasQuestionDateValue
          ? [new Date(data.configs.questions_start_date!), new Date(data.configs.questions_end_date!)]
          : ['', ''],
        feedback_form: {
          content: data.configs?.feedback_form?.content || '',
          forward_chat_history: data.configs?.feedback_form?.forward_chat_history || false,
          forward_chat_history_visible_text: (() => {
            const vt = data.configs?.feedback_form?.forward_chat_history_visible_text;
            if (!vt) return '';
            if (typeof vt === 'string') return vt;
            return vt[i18n.language] || vt['en'] || Object.values(vt)[0] || '';
          })(),
          forward_chat_history_ai_instruction: data.configs?.feedback_form?.forward_chat_history_ai_instruction || '',
        },
        digital_human_prompt: data.configs?.digital_human_prompt,
        voice_agent_prompt: data.configs?.voice_agent_prompt || '',
        enable_access_control: data.configs?.enable_access_control || false,
        access_control_allow_email_list: data.configs?.access_control_allow_email_list || [],
        preview_panel_api: data.configs?.preview_panel_api || '',
        enable_tts: data.configs?.enable_tts || false,
        enable_2d_digital_person: data.configs?.enable_2d_digital_person || false,
        qa_recommendation: data.configs?.qa_recommendation ?? true,
        disable_third_party_url_access: data.configs?.disable_third_party_url_access || false,
        canvas_type: data.configs?.canvas_type,
        enable_llm_metrics: data.configs?.enable_llm_metrics || false,
        enable_thinking: data.configs?.enable_thinking ?? false,
        enable_memory: data.configs?.enable_memory ?? false,
      };
      setRobotConfigs(configs);
      documentForm.setValues(configs);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const initRobotPlan = async () => {
    const { data } = await getBotQuota({ botId });
    setRobotPlanInfo(data);
  };

  useEffect(() => {
    if (!botId) return;
    initRobotInfo();
    initRobotPlan();
  }, [botId]);

  const submit = documentForm.onSubmit(submitDocument);
  const submitInNextTick = useSubmitFormAsync(submit);

  // 覆盖机器人配置表单的 onChange/onBlur 事件行为
  // 实现在用户操作表单项后的合适时机自动触发保存行为
  const onOverrideChangeForConfigForm = (prop: string) => {
    return (data?: any) => {
      // 如果检测到是输入框的 onBlur 事件，并且该行为是用户触发（因为发现引入的Polyfill也会触发一次 blur），那么触发保存行为
      if (data && data.type === 'blur') {
        if (data.isTrusted) {
          submitInNextTick();
        }

        // 如果是代码触发的 onBlur，返回，什么都不做。
        return;
      } else {
        // 如果是 onChange 事件被触发，说明用户操作了表单的 Switch/Checkbox/Select 等组件
        // 那么更新表单的值，然后触发保存行为

        documentForm.getInputProps(prop).onChange(data);

        // 如果值是数组，目前的表单项里，说明是日期范围选择器的值
        // 那么只有当两个日期值都被选择或两个都是空时，才触发保存行为
        // 否则说明选到一半，不触发。
        //
        // P.S. 未来如果用这种办法无法区分出日期范围选择器，那么可以使用当前函数传入的 prop 值来区分。
        if (Array.isArray(data) && data.filter(Boolean).length === 1) {
          return;
        }
        submitInNextTick();
      }
    };
  };

  const submitBaseInfo = robotBaseInfoForm.onSubmit(updateRobotBaseInfo);
  const submitBaseInfoInNextTick = useSubmitFormAsync(submitBaseInfo);

  // 覆盖机器人基本信息表单的 onChange/onBlur 事件行为
  const onOverrideChangeForRobotBaseInfoForm = (prop: string) => {
    return (data?: any) => {
      // 如果检测到是输入框的 onBlur 事件，并且该行为是用户触发（因为发现引入的Polyfill也会触发一次 blur），那么触发保存行为
      if (data && data.type === 'blur') {
        if (data.isTrusted) {
          submitBaseInfoInNextTick();
        }

        // 如果是代码触发的 onBlur，返回，什么都不做。
        return;
      } else {
        // 如果是 onChange 事件被触发，说明用户操作了表单的 Switch/Checkbox/Select 等组件
        // 那么更新表单的值，然后触发保存行为

        robotBaseInfoForm.getInputProps(prop).onChange(data);

        // 如果值是数组，目前的表单项里，说明是日期范围选择器的值
        // 那么只有当两个日期值都被选择或两个都是空时，才触发保存行为
        // 否则说明选到一半，不触发。
        //
        // P.S. 未来如果用这种办法无法区分出日期范围选择器，那么可以使用当前函数传入的 prop 值来区分。
        if (Array.isArray(data) && data.filter(Boolean).length === 1) {
          return;
        }
        submitBaseInfoInNextTick();
      }
    };
  };

  const handleApplyComplete = () => {
    // 在这里处理外部数据更新逻辑
    initRobotInfo(); // 例如，重新初始化机器人信息
  };

  return (
    <>
      <ContainerHeader title={t('page.settings_title')} />
      <LoadingOverlay visible={loading} />
      <form onSubmit={submit}>
        <Tabs value={activeTab} onChange={setActiveTab} className="mt-10">
          <Tabs.List className="rounded-lg mb-4">
            <Tabs.Tab value="general" className="">
              {t('settings.title.title_general')}
            </Tabs.Tab>
            <Tabs.Tab value="advanced" className="">
              {t('settings.title.title_advanced')}
            </Tabs.Tab>
            {canvasAvailable && (
              <CanAccess accessKey="enable_canvas">
                <Tabs.Tab value="canvas" className="">
                  Canvas
                </Tabs.Tab>
              </CanAccess>
            )}
            {robotInfo?.robot_type !== 'rag' && (
              <Tabs.Tab value="prompt" className="">
                {t('settings.title.title_digital_human')}
              </Tabs.Tab>
            )}
            <Tabs.Tab value="publish" className="">
              {t('settings.title.publish')}
            </Tabs.Tab>
            <Tabs.Tab value="consent" className="">
              {t('settings.title.consent')}
            </Tabs.Tab>
          </Tabs.List>
          <ScrollArea h={`calc(100vh - var(--mantine-spacing-md) * 12)`}>
            <Tabs.Panel value="general">
              <General
                documentForm={documentForm}
                onOverrideChangeForRobotBaseInfoForm={onOverrideChangeForRobotBaseInfoForm}
                robotBaseInfoForm={robotBaseInfoForm}
                onOverrideChangeForConfigForm={onOverrideChangeForConfigForm}
                showDangerZone={!isAssist && (!permissionType || permissionType === 'OWNER')}
              />
            </Tabs.Panel>
            <Tabs.Panel value="advanced">
              <Advanced
                datasetId={datasetId || ''}
                documentForm={documentForm}
                onOverrideChangeForConfigForm={onOverrideChangeForConfigForm}
                robotPlanInfo={robotPlanInfo}
                submitInNextTick={submitInNextTick}
                robotInfo={robotInfo}
              />
            </Tabs.Panel>
            {canvasAvailable && (
              <Tabs.Panel value="canvas">
                <CanvasForm
                  documentForm={documentForm}
                  onOverrideChangeForConfigForm={onOverrideChangeForConfigForm}
                  aiId={botId}
                />
              </Tabs.Panel>
            )}
            {/* 有数字人权限的账户才能设置 */}
            {digitalHumanAccess && (
              <Tabs.Panel value="prompt">
                <Prompt
                  robotInfo={robotInfo || ({} as UpdateRobotParams)}
                  setRobotInfo={setRobotInfo}
                  documentForm={documentForm}
                  onOverrideChangeForConfigForm={onOverrideChangeForConfigForm}
                  onApplyComplete={handleApplyComplete}
                />
              </Tabs.Panel>
            )}
            <Tabs.Panel value="publish">
              <Publish />
            </Tabs.Panel>
            <Tabs.Panel value="consent">
              <Consent />
            </Tabs.Panel>
          </ScrollArea>
        </Tabs>
      </form>
    </>
  );
};

export default Settings;
